class Slider {
  constructor(options = {}) {
    const o = options
    this.config = {
      selector: o.selector || '.multi-slider',
      duration: o.duration || 200,
      easing: o.easing || 'ease-out',
      threshold: o.threshold || 20,
      direction: o.direction || 'horizontal',
      dotsDirection: o.dotsDirection || null,
      dotsPosition: o.dotsPosition || null,
      dotsMargin: o.dotsMargin || '10px',
      onInit: o.onInit || (() => {}),
      onChange: o.onChange || (() => {})
    }

    this.selector = typeof this.config.selector === 'string'
      ? document.querySelector(this.config.selector) : this.config.selector
    if (!this.selector) throw new Error('Slider selector not found')

    this.isVertical = this.config.direction === 'vertical'
    this.innerElements = Array.from(this.selector.children)
    this.currentSlide = 0
    this.selectorSize = this.isVertical ? this.selector.offsetHeight : this.selector.offsetWidth
    this.sliderFrame = null
    this.dots = null
    this.pointerDown = false
    this.drag = { startX: 0, endX: 0, startY: 0, endY: 0, letItGo: null, preventClick: false }

    // Bind event handlers
    ;['resize', 'touchstart', 'touchend', 'touchmove', 'mousedown', 'mouseup', 'mouseleave', 'mousemove', 'click', 'dotClick']
      .forEach(name => this[`${name}Handler`] = this[`${name}Handler`].bind(this))

    this.init()
  }

  get transitionStyle() {
    return `transform ${this.config.duration}ms ${this.config.easing}`
  }

  get currentOffset() {
    return -this.currentSlide * this.selectorSize
  }

  init() {
    this.attachEvents()
    this.buildSliderFrame()
    this.addDots()
    this.updateDots()
    this.config.onInit.call(this)
  }

  buildSliderFrame() {
    this.selectorSize = this.isVertical ? this.selector.offsetHeight : this.selector.offsetWidth

    const { isVertical, innerElements } = this
    const count = innerElements.length
    const sizeProp = isVertical ? 'height' : 'width'

    this.sliderFrame = document.createElement('div')
    Object.assign(this.sliderFrame.style, {
      display: 'flex',
      flexDirection: isVertical ? 'column' : 'row',
      [sizeProp]: `${count * 100}%`,
      transition: this.transitionStyle
    })

    const fragment = document.createDocumentFragment()
    innerElements.forEach(element => {
      const wrapper = document.createElement('div')
      wrapper.style.flex = '0 0 auto'
      wrapper.style[sizeProp] = `${100 / count}%`
      if (isVertical) wrapper.style.width = '100%'
      wrapper.appendChild(element)
      fragment.appendChild(wrapper)
    })

    this.sliderFrame.appendChild(fragment)
    Object.assign(this.selector.style, { overflow: 'hidden', cursor: 'grab' })
    this.selector.innerHTML = ''
    this.selector.appendChild(this.sliderFrame)
    this.slideToCurrent()
  }

  addDots() {
    this.dots = document.createElement('div')
    this.dots.classList.add('slider-dots')

    for (let i = 0; i < this.innerElements.length; i++) {
      const dot = document.createElement('div')
      dot.classList.add('slider-dots__item')
      dot.dataset.index = i
      this.dots.appendChild(dot)
    }

    this.applyDotsStyles()

    const position = this.config.dotsPosition
    const parent = this.selector.parentNode
    if (position) {
      parent.style.position = 'relative'
      parent.appendChild(this.dots)
    } else {
      parent.insertBefore(this.dots, this.selector.nextSibling)
    }

    this.dots.addEventListener('click', this.dotClickHandler)
  }

  applyDotsStyles() {
    const { dotsDirection, dotsPosition, dotsMargin } = this.config
    const style = this.dots.style

    if (dotsDirection) {
      style.display = 'flex'
      style.flexDirection = dotsDirection === 'vertical' ? 'column' : 'row'
      style.alignItems = 'center'
      style.justifyContent = 'center'
    }

    if (dotsPosition) {
      Object.assign(style, {
        position: 'absolute',
        top: 'unset', bottom: 'unset', left: 'unset', right: 'unset',
        width: 'unset', transform: '', margin: '0'
      })

      const isVerticalPos = dotsPosition === 'left' || dotsPosition === 'right'
      style[dotsPosition] = dotsMargin || '0'
      style[isVerticalPos ? 'top' : 'left'] = '50%'
      style.transform = isVerticalPos ? 'translateY(-50%)' : 'translateX(-50%)'
    }
  }

  setDotsDirection(direction) {
    if (direction !== 'horizontal' && direction !== 'vertical') {
      throw new Error('Dots direction must be "horizontal" or "vertical"')
    }
    this.config.dotsDirection = direction
    this.applyDotsStyles()
  }

  setDotsPosition(position) {
    if (!['top', 'bottom', 'left', 'right', null].includes(position)) {
      throw new Error('Dots position must be "top", "bottom", "left", "right", or null')
    }
    this.config.dotsPosition = position

    if (this.dots) {
      const parent = this.selector.parentNode
      if (position) {
        parent.style.position = 'relative'
        parent.appendChild(this.dots)
      } else {
        this.dots.style.position = ''
        parent.insertBefore(this.dots, this.selector.nextSibling)
      }
      this.applyDotsStyles()
    }
  }

  setDotsMargin(margin) {
    this.config.dotsMargin = margin
    this.applyDotsStyles()
  }

  updateDots() {
    if (!this.dots) return
    this.dots.querySelectorAll('.slider-dots__item').forEach((dot, index) => {
      dot.classList.toggle('slider-dots__item--active', index === this.currentSlide)
    })
  }

  slideToCurrent(instant = false) {
    const offset = this.currentOffset
    const transform = this.isVertical
      ? `translate3d(0, ${offset}px, 0)`
      : `translate3d(${offset}px, 0, 0)`

    if (instant) {
      this.sliderFrame.style.transition = 'none'
      this.sliderFrame.style.transform = transform
      this.sliderFrame.offsetHeight // Force reflow
      this.sliderFrame.style.transition = this.transitionStyle
    } else {
      this.sliderFrame.style.transform = transform
    }
  }

  prev() {
    this.currentSlide > 0 ? this.goTo(this.currentSlide - 1) : this.slideToCurrent()
  }

  next() {
    this.currentSlide < this.innerElements.length - 1 ? this.goTo(this.currentSlide + 1) : this.slideToCurrent()
  }

  goTo(index) {
    const previousSlide = this.currentSlide
    this.currentSlide = Math.max(0, Math.min(index, this.innerElements.length - 1))
    this.slideToCurrent()
    this.updateDots()
    if (previousSlide !== this.currentSlide) this.config.onChange.call(this)
  }

  setDirection(direction) {
    if (direction !== 'horizontal' && direction !== 'vertical') {
      throw new Error('Direction must be "horizontal" or "vertical"')
    }
    this.config.direction = direction
    this.isVertical = direction === 'vertical'
    this.selectorSize = this.isVertical ? this.selector.offsetHeight : this.selector.offsetWidth

    const count = this.innerElements.length
    const v = this.isVertical
    Object.assign(this.sliderFrame.style, {
      flexDirection: v ? 'column' : 'row',
      width: v ? '' : `${count * 100}%`,
      height: v ? `${count * 100}%` : ''
    })

    Array.from(this.sliderFrame.children).forEach(wrapper => {
      wrapper.style.width = v ? '100%' : `${100 / count}%`
      wrapper.style.height = v ? `${100 / count}%` : ''
    })
    this.slideToCurrent()
  }

  updateAfterDrag() {
    const distance = this.isVertical ? (this.drag.endY - this.drag.startY) : (this.drag.endX - this.drag.startX)
    Math.abs(distance) > this.config.threshold ? (distance > 0 ? this.prev() : this.next()) : this.slideToCurrent()
  }

  clearDrag() {
    this.drag = { startX: 0, endX: 0, startY: 0, endY: 0, letItGo: null, preventClick: this.drag.preventClick }
  }

  applyDragTransform(x, y) {
    const offset = this.currentOffset
    const dragOffset = this.isVertical ? (y - this.drag.startY) : (x - this.drag.startX)
    this.sliderFrame.style.transition = 'none'
    this.sliderFrame.style.transform = this.isVertical
      ? `translate3d(0, ${offset + dragOffset}px, 0)` : `translate3d(${offset + dragOffset}px, 0, 0)`
  }

  endDrag() {
    this.pointerDown = false
    this.selector.style.cursor = 'grab'
    this.sliderFrame.style.transition = this.transitionStyle
    if (this.drag.endX || this.drag.endY) this.updateAfterDrag()
    this.clearDrag()
  }

  resizeHandler() {
    this.selectorSize = this.isVertical ? this.selector.offsetHeight : this.selector.offsetWidth
    this.slideToCurrent(true)
  }

  // Touch events
  touchstartHandler(e) {
    if (['TEXTAREA', 'OPTION', 'INPUT', 'SELECT'].includes(e.target.nodeName)) return
    e.stopPropagation()
    this.pointerDown = true
    this.drag.startX = e.touches[0].pageX
    this.drag.startY = e.touches[0].pageY
  }

  touchendHandler(e) {
    e.stopPropagation()
    this.endDrag()
  }

  touchmoveHandler(e) {
    e.stopPropagation()
    if (this.drag.letItGo === null) {
      const hMove = Math.abs(this.drag.startX - e.touches[0].pageX)
      const vMove = Math.abs(this.drag.startY - e.touches[0].pageY)
      this.drag.letItGo = this.isVertical ? vMove > hMove : hMove > vMove
    }
    if (this.pointerDown && this.drag.letItGo) {
      e.preventDefault()
      this.drag.endX = e.touches[0].pageX
      this.drag.endY = e.touches[0].pageY
      this.applyDragTransform(this.drag.endX, this.drag.endY)
    }
  }

  // Mouse events
  mousedownHandler(e) {
    if (['TEXTAREA', 'OPTION', 'INPUT', 'SELECT'].includes(e.target.nodeName)) return
    e.preventDefault()
    e.stopPropagation()
    this.pointerDown = true
    this.drag.startX = e.pageX
    this.drag.startY = e.pageY
  }

  mouseupHandler(e) {
    e.stopPropagation()
    this.endDrag()
  }

  mousemoveHandler(e) {
    if (!this.pointerDown) return
    e.preventDefault()
    if (e.target.nodeName === 'A') this.drag.preventClick = true
    this.drag.endX = e.pageX
    this.drag.endY = e.pageY
    this.selector.style.cursor = 'grabbing'
    this.applyDragTransform(e.pageX, e.pageY)
  }

  mouseleaveHandler(e) {
    if (!this.pointerDown) return
    this.drag.endX = e.pageX
    this.drag.endY = e.pageY
    this.drag.preventClick = false
    this.endDrag()
  }

  clickHandler(e) {
    if (this.drag.preventClick) e.preventDefault()
    this.drag.preventClick = false
  }

  dotClickHandler(e) {
    const dot = e.target.closest('.slider-dots__item')
    if (dot?.dataset.index) this.goTo(parseInt(dot.dataset.index, 10))
  }

  attachEvents() {
    window.addEventListener('resize', this.resizeHandler)
    const events = ['touchstart', 'touchend', 'touchmove', 'mousedown', 'mouseup', 'mouseleave', 'mousemove', 'click']
    events.forEach(evt => this.selector.addEventListener(evt, this[`${evt}Handler`]))
  }

  detachEvents() {
    window.removeEventListener('resize', this.resizeHandler)
    const events = ['touchstart', 'touchend', 'touchmove', 'mousedown', 'mouseup', 'mouseleave', 'mousemove', 'click']
    events.forEach(evt => this.selector.removeEventListener(evt, this[`${evt}Handler`]))
    if (this.dots) this.dots.removeEventListener('click', this.dotClickHandler)
  }

  destroy() {
    this.detachEvents()
    if (this.dots) {
      this.dots.remove()
      this.dots = null
    }
    const fragment = document.createDocumentFragment()
    this.innerElements.forEach(element => fragment.appendChild(element))
    this.selector.innerHTML = ''
    this.selector.appendChild(fragment)
    this.selector.removeAttribute('style')
  }
}
window.Slider = Slider
